import { Fragment, FunctionComponent, useEffect } from "react";
import { Hero } from "@App/Components/Hero";
import { BrandFilter } from "@App/Components/Filters";
import { ContactUs } from "@App/Components/ContactUs";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { useServices } from "@App/hooks/api/useServices";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useHome } from "@App/hooks/useHome";
import { Loader } from "@App/Components/Loader";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { SubServiceStep } from "@App/Pages/Partner/Partials/SubServiceStep";
import { EngineTypeStep } from "@App/Pages/Partner/Partials/EngineTypeStep";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import { ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored } from "@App/utils/conditionals/combinations";
import { TireServiceStep } from "@App/Pages/Partner/Partials/TireServiceStep";
import { SecondaryTireServiceStep } from "@App/Pages/Partner/Partials/SecondaryTireServiceStep";
import {
  ServiceIsGlass,
  ServiceIsWheelChange,
  ServiceIsWheelChangeTiresStored,
} from "@App/utils/conditionals/services";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { Step } from "@App/Components/Stepper/Step";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const location = useLocation();

  const { t } = useTranslation();

  // step definitions
  const locationStepProps = useLocationStepDefinition(onSubmit);

  const { loading, error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    selectBrand,
    selectEngine,
    togglePremiumService,
    togglePremiumServiceCarReplacementOrPickup,
    toggled,
    nextStep,
    organization,
    service,
    availableSubservices,
    availableSubservicesLoading,
    premiumServices,
    selectService,
    selectTireServiceTypeAndService,
    secondaryTireStepSelection,
    tireStepSelection,
    showSecondaryTireStep,
    bevSupport,
    brand,
    stepToOneAndResetOthers,
  } = useHome();

  useEffect(() => {
    if (Number.parseInt(window.location.hash.substr(1)) > 0) {
      setCurrentStep(Number.parseInt(window.location.hash.substr(1)));
    }
  }, [location]);

  //if service is available initially (ie. on browserback) or arrives async from the ?service= url param, set step to 1
  useEffect(() => {
    if (service && currentStep === 0) {
      stepToOneAndResetOthers();
    }
  }, [service]);

  //if only one service is available set service to that and skip selection step (this needs deps, because services are empty on initial pageload)
  useEffect(() => {
    if (services && services.length === 1 && !service) {
      selectService(services[0], true);
      setCurrentStep(1);
    }
  }, [services]);

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", { organization: organization.name })}
        backgroundImage={
          organization.settings.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service && service.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              services={services}
              loading={loading}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          {availableSubservicesLoading && <Loader></Loader>}
          {ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Welchen Service benötigen Sie?"}
                subtitle={null}
                completedTitle={"Reifenservice" + ": "}
                completedGoBackLinkTitle={tireStepSelection}
              >
                <TireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={false}
                />
              </Step>
            )}
          {showSecondaryTireStep &&
            ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Sind die Reifen eingelagert?"}
                subtitle={null}
                completedTitle={"Sind die Reifen eingelagert?" + ": "}
                completedGoBackLinkTitle={secondaryTireStepSelection}
              >
                <SecondaryTireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={true}
                />
              </Step>
            )}
          {(availableSubservices?.length > 0 || service == null) &&
            !availableSubservicesLoading && (
              <Step
                title={t("chooseSubServices")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices.length == 0 ? "Keine Auswahl" : ""} ${premiumServices.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableSubservices}
                  premiumServices={premiumServices}
                  toggled={toggled}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={togglePremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {!ServiceIsGlass(service) && !availableSubservicesLoading && (
            <Step
              title={t("Antriebsart")}
              subtitle={null}
              completedTitle={t("Antriebsart") + ": "}
              completedGoBackLinkTitle={
                bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")
              }
            >
              <EngineTypeStep selectEngine={selectEngine} />
            </Step>
          )}
          {!ServiceIsWheelChange(service) &&
            !ServiceIsGlass(service) &&
            !ServiceIsWheelChangeTiresStored(service) && (
              <Step
                title={t("Waehlen Sie Ihre Marke.")}
                completedTitle={"Marke: "}
                completedGoBackLinkTitle={brand ? brand.name : ""}
              >
                <Fragment>
                  <BrandFilter onSubmit={selectBrand} />
                </Fragment>
              </Step>
            )}
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              <Benefits organization={organization} />
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
